/**
 * Per-script font stacks (Deliverable C, finalized in Phase 3) — `fontStack(script)` of §6.5.
 *
 * We return a CSS `font-family` fallback chain per script and delegate *glyph shaping*
 * (ligatures, the Arabic joining forms, Devanagari conjuncts/reordering) to the platform
 * shaper — L5 never measures or slices glyphs. Each chain follows the same documented
 * ordering discipline and ALWAYS terminates in a CSS generic family keyword, so no script
 * can ever resolve to an empty `font-family`:
 *
 *   [pan-script preferred (Noto family)] → [platform-native fonts] → [system UI] → [generic]
 *
 * - `Noto Sans <Script>` leads each non-Latin chain: it is the broadest freely-licensable
 *   family with full coverage of the target script, ideal as a downloadable web-font and a
 *   common system install.
 * - Platform-native fonts (Geeza Pro / Kohinoor on Apple, Segoe UI / Mangal on Windows)
 *   come next so an OS that ships a high-quality script font is preferred over a download.
 * - The shared `SANS_FALLBACK` (system-ui … sans-serif) is appended so the chain degrades
 *   to whatever the OS uses for UI text and finally to the `sans-serif` generic.
 *
 * The English path needs no font downloads; Arabic and Devanagari are release-grade.
 */
/** Shared tail: platform UI fonts ending in the `sans-serif` generic (guaranteed terminator). */
const SANS_FALLBACK = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const LATIN_STACK = `'Inter', ${SANS_FALLBACK}`;
/** Script → ordered font-family fallback chain (each ends in a CSS generic family). */
const FONT_STACKS = {
    // Latin / default UI text (the Latin chain also covers Greek + Cyrillic glyphs via the
    // system UI fonts, but those scripts get explicit entries below for clarity + Noto lead).
    latin: LATIN_STACK,
    // Arabic: Noto → Apple (Geeza Pro) → Windows (Segoe UI/Tahoma) → system → sans-serif.
    arabic: `'Noto Sans Arabic', 'Geeza Pro', 'Segoe UI', Tahoma, ${SANS_FALLBACK}`,
    // Devanagari: Noto → Apple (Kohinoor) → Windows (Mangal) → system → sans-serif.
    devanagari: `'Noto Sans Devanagari', 'Kohinoor Devanagari', Mangal, ${SANS_FALLBACK}`,
    // Han (CJK, Simplified-leaning default): Noto → Apple → Windows → system → sans-serif.
    han: `'Noto Sans CJK SC', 'PingFang SC', 'Microsoft YaHei', 'Hiragino Sans', ${SANS_FALLBACK}`,
    // Japanese Han variant: Noto JP → Apple (Hiragino) → Windows (Yu Gothic) → system.
    'han-ja': `'Noto Sans CJK JP', 'Hiragino Sans', 'Yu Gothic', 'Meiryo', ${SANS_FALLBACK}`,
    // Korean Han variant: Noto KR → Apple (Apple SD Gothic) → Windows (Malgun) → system.
    'han-ko': `'Noto Sans CJK KR', 'Apple SD Gothic Neo', 'Malgun Gothic', ${SANS_FALLBACK}`,
    // Hebrew (RTL): Noto → Apple (Arial Hebrew) → Windows (Gisha/Segoe) → system → sans-serif.
    hebrew: `'Noto Sans Hebrew', 'Arial Hebrew', Gisha, 'Segoe UI', ${SANS_FALLBACK}`,
    // Thai: Noto → Apple (Thonburi) → Windows (Leelawadee UI/Tahoma) → system → sans-serif.
    thai: `'Noto Sans Thai', Thonburi, 'Leelawadee UI', Tahoma, ${SANS_FALLBACK}`,
    // Bengali: Noto → Apple (Kohinoor Bangla) → Windows (Nirmala UI/Vrinda) → system.
    bengali: `'Noto Sans Bengali', 'Kohinoor Bangla', 'Nirmala UI', Vrinda, ${SANS_FALLBACK}`,
    // Tamil: Noto → Apple (Tamil MN/Tamil Sangam MN) → Windows (Nirmala UI/Latha) → system.
    tamil: `'Noto Sans Tamil', 'Tamil Sangam MN', 'Nirmala UI', Latha, ${SANS_FALLBACK}`,
    // Telugu: Noto → Apple (Kohinoor Telugu) → Windows (Nirmala UI/Gautami) → system.
    telugu: `'Noto Sans Telugu', 'Kohinoor Telugu', 'Nirmala UI', Gautami, ${SANS_FALLBACK}`,
    // Hangul (Korean): Noto → Apple (Apple SD Gothic) → Windows (Malgun Gothic) → system.
    hangul: `'Noto Sans KR', 'Apple SD Gothic Neo', 'Malgun Gothic', ${SANS_FALLBACK}`,
    // Greek: Noto → system UI (which carries Greek) → sans-serif. (Latin chain also suffices.)
    greek: `'Noto Sans', ${SANS_FALLBACK}`,
    // Armenian: Noto → Apple (Mshtakan) → Windows (Sylfaen) → system → sans-serif.
    armenian: `'Noto Sans Armenian', Mshtakan, Sylfaen, ${SANS_FALLBACK}`,
    // Ethiopic (Geʽez, for Amharic/Tigrinya): Noto → Apple (Kefa) → Windows (Nyala) → system.
    ethiopic: `'Noto Sans Ethiopic', Kefa, Nyala, 'Abyssinica SIL', ${SANS_FALLBACK}`,
    // Cyrillic: Noto → system UI (which carries Cyrillic) → sans-serif.
    cyrillic: `'Noto Sans', ${SANS_FALLBACK}`,
    // Thaana (Divehi, RTL): Noto → Windows (MV Boli) → system → sans-serif.
    thaana: `'Noto Sans Thaana', 'MV Boli', ${SANS_FALLBACK}`,
    // N'Ko (RTL): Noto → system → sans-serif.
    nko: `'Noto Sans NKo', ${SANS_FALLBACK}`,
    // Mongolian (vertical script; shaping delegated to the platform): Noto → system.
    mongolian: `'Noto Sans Mongolian', 'Mongolian Baiti', ${SANS_FALLBACK}`,
    // Monospace chain for technical ids / digests (kept LTR via bidi isolation).
    mono: "'JetBrains Mono', ui-monospace, 'SF Mono', 'Cascadia Code', Menlo, Consolas, monospace",
};
const DEFAULT_STACK = LATIN_STACK;
/**
 * The fallback chain for a script. Unknown scripts fall back to the Latin/system chain so
 * a new script never yields an empty `font-family`.
 */
export function fontStackFor(script) {
    return FONT_STACKS[script] ?? DEFAULT_STACK;
}
/** The scripts with a dedicated stack (for tests / introspection). */
export const KNOWN_SCRIPTS = Object.keys(FONT_STACKS);
//# sourceMappingURL=font-stacks.js.map